<script setup lang="ts">
/**
 * Подъём стенда с экрана раздела: репозиторий, ветка и задача.
 *
 * В карточке задачи ветку выбирают из известных трекеру, здесь её вводят
 * руками — экран раздела не привязан к задаче. Задачу спрашиваем ключом и
 * находим по нему: сервису нужен идентификатор, а человек знает «DEV-1476».
 * Ключ подставляется из имени ветки — по договорённости ветка начинается с
 * него, и переспрашивать то, что уже написано, незачем.
 */
import { AppButton, AppModal, AppTextField } from '@vinprop/design-system'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { raiseStand } from '../api'
import { getIssueByKey } from '@/features/issue/api'

const emit = defineEmits<{ done: []; close: [] }>()

const { t } = useI18n()

const projectPath = ref('')
const branch = ref('')
const issueKey = ref('')
const busy = ref(false)
const error = ref('')

/** Ключ из имени ветки: `DEV-1476-devops` → `DEV-1476`. */
const keyFromBranch = (name: string) => name.trim().match(/^[A-Z]+-\d+/)?.[0] ?? ''

watch(branch, (value) => {
  const guessed = keyFromBranch(value)

  if (guessed) issueKey.value = guessed
})

const isValid = computed(
  () => projectPath.value.trim() && branch.value.trim() && issueKey.value.trim(),
)

const submit = async () => {
  if (!isValid.value) return

  busy.value = true
  error.value = ''

  try {
    const issue = await getIssueByKey(issueKey.value.trim())

    if (!issue) {
      error.value = t('devops.envs.raise.unknownIssue')

      return
    }

    await raiseStand(issue.id, projectPath.value.trim(), branch.value.trim())
    emit('done')
  } catch {
    error.value = t('devops.envs.actionError')
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <AppModal
    model-value
    :title="t('devops.envs.raise.title')"
    size="small"
    @update:model-value="emit('close')"
  >
    <div class="raise">
      <p class="raise__hint">{{ t('devops.envs.raise.hint') }}</p>

      <AppTextField
        v-model="projectPath"
        :label="t('devops.envs.raise.service')"
        placeholder="vinprop/frontend/tracker-vue"
      />
      <AppTextField
        v-model="branch"
        :label="t('devops.envs.raise.branch')"
        placeholder="DEV-1476-devops"
      />
      <AppTextField
        v-model="issueKey"
        :label="t('devops.envs.raise.issue')"
        placeholder="DEV-1476"
        :error="error"
      />
    </div>

    <template #footer>
      <AppButton variant="secondary" @click="emit('close')">
        {{ t('common.actions.cancel') }}
      </AppButton>
      <AppButton :loading="busy" :disabled="!isValid" @click="submit">
        {{ t('devops.stand.raise') }}
      </AppButton>
    </template>
  </AppModal>
</template>

<style scoped>
.raise {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.raise__hint {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body-sm);
}
</style>
